// Phase 5 — admin-only Google Calendar page.
//
// Drives the shared Creative Mojo calendar via the FastAPI proxy. Three
// states:
//   1. Backend not configured (env vars missing) → setup hints
//   2. Configured but disconnected → Connect button (opens Google's consent
//      screen; on return the page re-loads with ?connected=1 and we refresh)
//   3. Connected → list view + create/edit modal
//
// Events optionally carry a `meeting_url` (typically an MS Teams join link).
// We render it as a "Join meeting" button on each row.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { CalendarDays, ExternalLink, Loader2, Plus, RefreshCw, Trash2, AlertCircle, CheckCircle2, X, Save, Link as LinkIcon, MapPin, Clock, Pencil, PowerOff, Video, LayoutGrid, LayoutList, Users, Sparkles, Search, BookOpen } from "lucide-react";
import YearlyEventsModal from "@/components/calendar/YearlyEventsModal";
import ProjectsThisMonthModal from "@/components/calendar/ProjectsThisMonthModal";

function formatDateRange(start, end, allDay) {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  const sameDay = s.toDateString() === e.toDateString();
  const opts = allDay
    ? { weekday: "short", day: "2-digit", month: "short", year: "numeric" }
    : { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false };
  if (allDay) return sameDay ? s.toLocaleDateString("en-GB", opts) : `${s.toLocaleDateString("en-GB", opts)} → ${e.toLocaleDateString("en-GB", opts)}`;
  if (sameDay) {
    const dayPart = s.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
    const timeOpts = { hour: "2-digit", minute: "2-digit", hour12: false };
    const timePart = `${s.toLocaleTimeString("en-GB", timeOpts)} – ${e.toLocaleTimeString("en-GB", timeOpts)}`;
    return `${dayPart} · ${timePart}`;
  }
  return `${s.toLocaleString("en-GB", opts)} → ${e.toLocaleString("en-GB", opts)}`;
}

function todayLocal(offsetMinutes = 0) {
  const d = new Date(Date.now() + offsetMinutes * 60 * 1000);
  // datetime-local needs YYYY-MM-DDTHH:MM, no seconds, no zone
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CalendarPage() {
  const [search, setSearch] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  // Legend-driven multi-select filter. Every kind is on by default.
  // Toggling a legend button flips membership in this Set; the
  // ``fcEvents`` memo below filters out disabled kinds so admins can
  // e.g. isolate all Training Days for August by turning everything
  // else off. Persists on component state, not URL — a page reload
  // resets to Show All (matches admin expectation that filters are
  // exploratory, not shareable).
  const [enabledKinds, setEnabledKinds] = useState(
    () => new Set(["hq", "mojo_grow", "initial_franchisee_meeting", "yearly", "shadow_day", "training_day"])
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // null | { event? }
  const [refreshTick, setRefreshTick] = useState(0);
  const [yearlyOpen, setYearlyOpen] = useState(false);
  // "Projects this month" — opens the same Standard Boxed Art Kits
  // modal franchisees see on their portal, scoped to whichever month
  // the admin is currently browsing in the FullCalendar grid. Lets
  // admins quickly sanity-check that the right kits are linked
  // (Project Code → Instruction PDF) for the visible month.
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [visibleDate, setVisibleDate] = useState(() => new Date());
  // HQ-managed yearly events (CSV-uploaded). Rendered on the admin
  // grid alongside Google events in the same light-blue swatch the
  // franchisee portal uses so admins see exactly what franchisees see.
  const [yearlyEvents, setYearlyEvents] = useState([]);
  // Search query — filters which events render in the grid + list view.
  // Matches event title / location / description / summary
  // (case-insensitive substring) so admins can find any HQ training or
  // yearly fixture in seconds without scrolling 12 months of calendar.
  const [query, setQuery] = useState("");
  // View mode persists across visits so each admin lands back where they left.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("calendar.view") || "grid"; }
    catch { return "grid"; }
  });
  useEffect(() => { try { localStorage.setItem("calendar.view", view); } catch (_) { /* noop */ } }, [view]);
  const fcRef = useRef(null);

  // Responsive FullCalendar height. On landscape phones / small tablets
  // the fixed 860px eats the whole viewport and pushes controls
  // off-screen, so we tie the height to the actual viewport (min 380
  // for landscape phones, max 860 for desktops) and re-measure on
  // resize/rotate. Also drops toolbar buttons in the header to a
  // compact set below 700px so the two-row title+buttons don't wrap
  // awkwardly.
  const [viewport, setViewport] = useState(() => (
    typeof window !== "undefined"
      ? { w: window.innerWidth, h: window.innerHeight }
      : { w: 1280, h: 900 }
  ));
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  const isNarrow = viewport.w < 900;
  const isLandscapePhone = viewport.h < 500;
  // Cap at 860 for desktops; on small screens leave ~180px for the
  // top toolbar/legend/action row so the calendar body fits inside
  // one viewport without a page-level scroll.
  const fcHeight = isNarrow
    ? Math.max(380, Math.min(viewport.h - 180, 720))
    : 860;

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/calendar/status");
      setStatus(data);
      return data;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load calendar status");
      return null;
    }
  };

  const loadEvents = async (range = null) => {
    // First load = blocking spinner; subsequent navigations = silent refresh
    // so the FullCalendar grid doesn't disappear every time the admin clicks
    // prev / next.
    setEvents((prev) => prev); // no-op to keep dep tidy
    if (events.length === 0) setLoading(true);
    else setRefreshing(true);
    setErr("");
    try {
      const params = range
        ? { time_min: range.start.toISOString(), time_max: range.end.toISOString() }
        : { days_back: 365, days_ahead: 365 };
      const { data } = await api.get("/calendar/events", { params });
      setEvents(data.events || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not load events";
      setEvents([]); setErr(detail);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    (async () => {
      const s = await loadStatus();
      if (s?.connected) await loadEvents();
      else setLoading(false);
      // Yearly events are admin-managed but live in Mongo, so we can
      // load them regardless of the Google connection state.
      try {
        const { data } = await api.get("/admin/calendar/yearly-events");
        setYearlyEvents(data.items || []);
      } catch { /* swallow — calendar still works without them */ }
    })();
  }, [refreshTick]);

  // After OAuth redirect: clear the ?connected=1 query param + refresh
  useEffect(() => {
    if (search.get("connected") === "1") {
      search.delete("connected");
      setSearch(search, { replace: true });
      setRefreshTick((t) => t + 1);
    }
    if (search.get("error")) {
      setErr(`Google OAuth failed: ${search.get("error")}`);
      search.delete("error");
      setSearch(search, { replace: true });
    }
  }, [search, setSearch]);

  const connect = async () => {
    try {
      const { data } = await api.get("/calendar/auth-url");
      window.location.href = data.url;
    } catch (e) { setErr(e?.response?.data?.detail || "Could not start OAuth"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Existing events stay in Google; we just stop accessing them.")) return;
    await api.post("/calendar/disconnect");
    setRefreshTick((t) => t + 1);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event from the calendar?")) return;
    try {
      await api.delete(`/calendar/events/${id}`);
      setEvents((es) => es.filter((e) => e.id !== id));
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };

  // Search query needle — shared by `grouped` (list view) and
  // `fcEvents` (grid view). Declared once up here so both memos can
  // reference it without tripping over temporal-dead-zone.
  const queryNeedle = (query || "").trim().toLowerCase();
  const matchesQuery = (e) => {
    if (!queryNeedle) return true;
    const hay = [e.title, e.location, e.description, e.summary, e.notes]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(queryNeedle);
  };

  // Whenever the search query changes, jump the FullCalendar grid to
  // the EARLIEST matching event's month so the user instantly sees
  // what they searched for (rather than being stranded on the current
  // month with an empty grid). Empty query leaves the view alone.
  useEffect(() => {
    if (!queryNeedle || !fcRef.current) return;
    const candidates = [];
    events.forEach((e) => { if (matchesQuery(e) && e.start) candidates.push(e.start); });
    yearlyEvents.forEach((y) => { if (matchesQuery(y) && y.date_iso) candidates.push(y.date_iso); });
    if (!candidates.length) return;
    // Bias towards future events first — admins are usually searching
    // for "what's next", not "what's already happened".
    const today = new Date().toISOString().slice(0, 10);
    const future = candidates.filter((d) => d.slice(0, 10) >= today).sort();
    const past = candidates.filter((d) => d.slice(0, 10) < today).sort().reverse();
    const target = future[0] || past[0];
    if (target) {
      try { fcRef.current.getApi().gotoDate(target); } catch { /* noop */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryNeedle, events, yearlyEvents]);

  const grouped = useMemo(() => {
    // Bucket events by their day label. Two-tier sort: upcoming days
    // (today + future) come first in chronological order, then past
    // days afterwards in reverse-chronological (newest-first) order. This
    // means when Sandra opens the list, the next thing she has to do is
    // at the very top — not buried under last year's recurring meetings.
    //
    // Search-aware: when a query is active we narrow the input to
    // events that hit the substring so the listed view shrinks to
    // matches only, matching the grid behaviour.
    const buckets = new Map();
    const dayKeys = new Map(); // label → timestamp for sorting
    // Build a merged list — Google events + yearly events — so the
    // list view shows everything the grid does. Each entry carries
    // `_kind` so the renderer can style them differently.
    const merged = [];
    events.filter(matchesQuery).forEach((e) => merged.push({ ...e, _kind: e._kind || "hq" }));
    yearlyEvents.filter(matchesQuery).forEach((y) => {
      merged.push({
        ...y,
        _kind: "yearly",
        // Normalise into the shape the loop expects.
        start: y.date_iso,
        all_day: true,
      });
    });
    merged.forEach((e) => {
      if (!e.start) return;
      const d = new Date(e.start);
      const label = d.toLocaleDateString("en-GB", {
        weekday: "long", day: "2-digit", month: "long", year: "numeric",
      });
      // Day-only timestamp (midnight) for stable sorting regardless of
      // intra-day order.
      const dayTs = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      if (!buckets.has(label)) {
        buckets.set(label, []);
        dayKeys.set(label, dayTs);
      }
      buckets.get(label).push(e);
    });
    // Within each day, sort events by start time ascending so 09:00 is
    // above 14:00.
    for (const list of buckets.values()) {
      list.sort((a, b) => new Date(a.start) - new Date(b.start));
    }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayTs = today.getTime();
    const entries = [...buckets.entries()];
    entries.sort((a, b) => {
      const aTs = dayKeys.get(a[0]); const bTs = dayKeys.get(b[0]);
      const aFuture = aTs >= todayTs;
      const bFuture = bTs >= todayTs;
      if (aFuture !== bFuture) return aFuture ? -1 : 1;
      return aFuture ? aTs - bTs : bTs - aTs;
    });
    return entries;
  }, [events, yearlyEvents, queryNeedle]);

  // Kind → colour + human label map. Central source of truth for both
// the calendar event styling AND the legend/filter buttons above the
// grid so a colour tweak lives in one place. Sam's Feb 2026 brief:
// Shadow Day → bright pink, Training Day → bright yellow (distinct
// from the lime HQ default). Legend doubles as a multi-select filter
// so admins can e.g. "show me only Training Days for August".
const KIND_STYLES = {
  hq:                          { label: "HQ events (Google Calendar)", bg: "#dddd16", border: "#7a7a0c", text: "#1c1917" },
  mojo_grow:                   { label: "Mojo Grow meetings",          bg: "#9f205b", border: "#7a1844", text: "#FFFFFF" },
  initial_franchisee_meeting:  { label: "Initial franchisee meeting",  bg: "#F97316", border: "#C2410C", text: "#FFFFFF" },
  yearly:                      { label: "Yearly events",               bg: "#3B82F6", border: "#1D4ED8", text: "#FFFFFF" },
  shadow_day:                  { label: "Shadow Day",                  bg: "#ec4899", border: "#be185d", text: "#FFFFFF" },
  training_day:                { label: "Training Day",                bg: "#facc15", border: "#a16207", text: "#1c1917" },
};
// The order matters for the legend row.
const KIND_ORDER = [
  "hq", "mojo_grow", "initial_franchisee_meeting",
  "yearly", "shadow_day", "training_day",
];



  // For all-day events Google returns YYYY-MM-DD which FC handles natively;
  // timed events come back as ISO 8601 with tz offset, also fine.
  //
  // Search-aware: when the admin types into the search box we filter
  // both Google events and yearly events by title / location /
  // description / summary substring so the grid + list view shrink
  // to just matches. The needle + match helper are declared once at
  // the top of the component body (see above) so this memo just
  // applies the filter.
  const fcEvents = useMemo(() => {
    // "Mojo Grow" events get their own brand-mauve treatment so the
    // monthly grow-team huddles stand out from the lime HQ events.
    // Detected by title substring (case-insensitive) — keeps it
    // zero-config; once HQ tags events with proper categories we can
    // graduate this to a metadata flag.
    const isMojoGrow = (e) => /mojo\s*grow/i.test(e?.title || "");
    // Shadow / Training day detection — matches the calendar events
    // produced by ``checklist_calendar_sync`` on the backend. Titles
    // land as ``Shadow Day · <Name>`` / ``Training Day · <Name>`` so
    // a simple title prefix + extendedProperties check is enough. We
    // fall back to extendedProperties.shared.checklist_kind for the
    // authoritative match (Google preserves those through edits).
    const kindOf = (e) => {
      const t = e?.title || "";
      const meta = (e?.checklist_kind
        || e?.raw?.extendedProperties?.shared?.checklist_kind
        || e?.extendedProperties?.shared?.checklist_kind || "").toLowerCase();
      if (meta === "shadow_day") return "shadow_day";
      if (meta === "training_day") return "training_day";
      if (/^shadow day\b/i.test(t)) return "shadow_day";
      if (/^training day\b/i.test(t)) return "training_day";
      if (isMojoGrow(e)) return "mojo_grow";
      if (e?.initial_franchisee_meeting) return "initial_franchisee_meeting";
      return "hq";
    };

    const out = events.filter(matchesQuery).map((e) => {
      const kind = kindOf(e);
      const style = KIND_STYLES[kind] || KIND_STYLES.hq;
      return {
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end,
        allDay: !!e.all_day,
        extendedProps: { ...e, _kind: kind },
        backgroundColor: style.bg,
        borderColor: style.border,
        textColor: style.text,
        display: "block",
      };
    }).filter((ev) => enabledKinds.has(ev.extendedProps._kind));
    // Yearly events get the same light-blue solid block the franchisee
    // portal uses (so admins can verify visual parity at a glance).
    if (enabledKinds.has("yearly")) {
      yearlyEvents.forEach((y) => {
        if (!matchesQuery(y)) return;
        const style = KIND_STYLES.yearly;
        out.push({
          id: `yr-${y.id}`,
          title: y.title,
          start: y.date_iso,
          allDay: true,
          extendedProps: { ...y, _kind: "yearly" },
          backgroundColor: style.bg,
          borderColor: style.border,
          textColor: style.text,
          display: "block",
        });
      });
    }
    return out;
  }, [events, yearlyEvents, queryNeedle, enabledKinds]);

  return (
    <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-7 mx-auto w-full" data-testid="calendar-page">
      <div className="flex items-center justify-between gap-3 mb-4 sm:mb-7 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-4xl text-stone-950 flex items-center gap-2 sm:gap-3">
            <CalendarDays className="w-5 h-5 sm:w-7 sm:h-7" /> Calendar
          </h1>
          <p className="hidden sm:block text-sm text-stone-600 mt-1">Live view of the shared Creative Mojo Google Calendar.</p>
          {/* Left-column workflow shortcuts — Yearly events + Projects
              this month. They live under the page title (rather than in
              the top-right toolbar) so the toolbar stays reserved for
              event-creation + view controls. */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button onClick={() => setYearlyOpen(true)} data-testid="cal-yearly-events-btn"
              title="Manage the yearly events that appear on every franchisee portal calendar"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 rounded-lg flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Yearly events
            </button>
            <button onClick={() => setProjectsModalOpen(true)} data-testid="cal-projects-month-btn"
              title="See the projects available this month"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-[#dedd0a] rounded-lg flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> View projects for this month
            </button>
          </div>
        </div>
        {/* Right-column toolbar — two rows.
            Row 1: primary event-creation actions (+ New event, then
                   Create New Mojo Grow Meeting to its right).
            Row 2: connection pill + view toggle + refresh, kept
                   secondary because they're view controls, not create
                   actions. */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {status?.connected && (
              <button onClick={() => setModal({ event: null })} data-testid="cal-new-event"
                className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> New event
              </button>
            )}
            <button
              onClick={() => setModal({ event: null, preset: "mojo_grow" })}
              data-testid="cal-new-mojo-grow"
              title="Create a Mojo Grow Meeting — auto-creates Zoom and sends an HQ Update to all active franchisees"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#c2c213] rounded-lg inline-flex items-center gap-1.5 shadow-sm"
            >
              <Video className="w-3.5 h-3.5" /> Create New Mojo Grow Meeting
            </button>
          </div>
          {status?.connected && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900" data-testid="cal-connected-pill">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="font-mono text-[11px]">{status.calendar_id}</span>
                <button onClick={disconnect} title="Disconnect Google Calendar"
                  data-testid="cal-disconnect" className="ml-1 text-stone-500 hover:text-red-700">
                  <PowerOff className="w-3 h-3" />
                </button>
              </div>
              <div className="inline-flex border border-stone-300 rounded-lg overflow-hidden" data-testid="cal-view-toggle">
                <button onClick={() => setView("grid")} data-testid="cal-view-grid"
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${view === "grid" ? "bg-stone-950 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}>
                  <LayoutGrid className="w-3.5 h-3.5" /> Calendar
                </button>
                <button onClick={() => setView("list")} data-testid="cal-view-list"
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${view === "list" ? "bg-stone-950 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}>
                  <LayoutList className="w-3.5 h-3.5" /> List
                </button>
              </div>
              <button
                onClick={() => {
                  const fcApi = fcRef.current?.getApi?.();
                  if (view === "grid" && fcApi) {
                    const v = fcApi.view;
                    const start = new Date(v.activeStart);
                    start.setDate(start.getDate() - 7);
                    const end = new Date(v.activeEnd);
                    end.setDate(end.getDate() + 7);
                    loadEvents({ start, end });
                  } else {
                    loadEvents();
                  }
                }}
                disabled={refreshing || loading}
                className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white hover:bg-stone-50 rounded-lg flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="cal-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search panel — filters events on title, location, description
          across the grid + list views + the yearly-only fallback below.
          Sits below the page header so it's always visible regardless
          of Google connection state. */}
      {(status?.connected || yearlyEvents.length > 0) && (
        <div className="mb-5 flex items-stretch gap-2" data-testid="cal-search">
          <div className="relative flex-1 max-w-xl">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search events by name, location, or description…"
              data-testid="cal-search-input"
              className="w-full pl-10 pr-10 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950 bg-white"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="cal-search-clear"
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 inline-flex items-center justify-center rounded-md hover:bg-stone-100 text-stone-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {query && (
            <div
              className="px-3 py-2.5 rounded-xl text-[11px] font-bold uppercase tracking-wider bg-stone-100 text-stone-700 flex items-center"
              data-testid="cal-search-count"
            >
              {fcEvents.length} match{fcEvents.length === 1 ? "" : "es"}
            </div>
          )}
        </div>
      )}

      {/* Not configured (no env vars) */}
      {status && !status.configured && (
        <SetupPanel status={status} />
      )}

      {/* Configured but disconnected */}
      {status?.configured && !status.connected && (
        <>
          <div className="bg-white border border-stone-200 rounded-2xl p-8 text-center max-w-xl mx-auto" data-testid="cal-connect">
            <CalendarDays className="w-12 h-12 mx-auto text-stone-300" />
            <h2 className="font-display text-2xl text-stone-950 mt-3">Connect Google Calendar</h2>
            <p className="text-sm text-stone-600 mt-2">
              Click below to authorise this admin console to read and write events on{" "}
              <strong className="text-stone-900">{status.calendar_id || "your shared calendar"}</strong>. You'll be redirected to Google to grant access, then sent back here.
            </p>
            <button onClick={connect} data-testid="cal-connect-btn"
              className="mt-5 px-5 py-3 text-sm font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Connect Google Calendar
            </button>
            {err && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</div>}
          </div>
          {/* Even when Google is disconnected we still surface the
              yearly events on a standalone grid so admins can preview
              what franchisees will see on their portal calendars. */}
          {yearlyEvents.length > 0 && (
            <div className="admin-cal-wrap mt-8 bg-white border border-stone-200 rounded-2xl p-2 sm:p-4" data-testid="cal-yearly-only-grid">
              <div className="flex items-center gap-2 mb-3 text-[11px] text-stone-600">
                <span className="w-3 h-3 rounded-sm" style={{ background: "#3B82F6" }} />
                <span>Yearly events ({yearlyEvents.length}) — visible to every franchisee on the portal</span>
              </div>
              <FullCalendar
                ref={fcRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth" }}
                events={fcEvents}
                height={isNarrow ? Math.max(380, Math.min(viewport.h - 180, 700)) : 820}
                contentHeight={isNarrow ? Math.max(380, Math.min(viewport.h - 180, 700)) : 820}
                firstDay={1}
                weekNumbers={false}
                dayMaxEventRows={isNarrow ? 2 : 6}
                buttonText={{ today: "Today", month: "Month" }}
                eventClick={(info) => { info.jsEvent.preventDefault(); setYearlyOpen(true); }}
              />
            </div>
          )}
        </>
      )}

      {/* Connected — events list */}
      {status?.connected && (
        <>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading events…</div>
          ) : err ? (
            <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          ) : view === "grid" ? (
            <div className="admin-cal-wrap bg-white border border-stone-200 rounded-2xl p-2 sm:p-4" data-testid="cal-grid">
              {/* Legend + filter — mirrors the franchisee portal
                  colours and doubles as a multi-select filter so
                  admins can isolate e.g. only Training Days for
                  August. Sam's Feb 2026 brief. */}
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="cal-legend">
                {KIND_ORDER.map((kind) => {
                  const style = KIND_STYLES[kind];
                  const active = enabledKinds.has(kind);
                  return (
                    <button
                      key={kind}
                      type="button"
                      onClick={() => setEnabledKinds((s) => {
                        const next = new Set(s);
                        if (next.has(kind)) next.delete(kind); else next.add(kind);
                        return next;
                      })}
                      data-testid={`cal-legend-${kind}`}
                      title={active ? `Hide ${style.label}` : `Show ${style.label}`}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border transition-all ${
                        active
                          ? "border-stone-400 bg-white text-stone-800 shadow-sm"
                          : "border-stone-200 bg-stone-50 text-stone-400 line-through"
                      }`}
                    >
                      <span
                        className="w-3 h-3 rounded-sm shrink-0"
                        style={{ background: style.bg, border: `1px solid ${style.border}`, opacity: active ? 1 : 0.35 }}
                      />
                      {style.label}
                    </button>
                  );
                })}
                <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] text-stone-600">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ boxShadow: "inset 0 0 0 2px #3B82F6" }} />
                  Today
                </span>
                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEnabledKinds(new Set(KIND_ORDER))}
                    data-testid="cal-legend-show-all"
                    disabled={enabledKinds.size === KIND_ORDER.length}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-stone-300 rounded-md text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Show every event type"
                  >
                    Show All
                  </button>
                  <button
                    type="button"
                    onClick={() => setEnabledKinds(new Set())}
                    data-testid="cal-legend-clear-all"
                    disabled={enabledKinds.size === 0}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-stone-300 rounded-md text-stone-700 bg-white hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Hide every event type"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <FullCalendar
                ref={fcRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={isNarrow ? {
                  // Compact landscape/mobile toolbar — title on its own
                  // row (auto-wraps below the button strip since FC
                  // stacks toolbar rows when they overflow), nav + view
                  // switcher stay one-tap accessible.
                  left: "prev,next",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek",
                } : {
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                events={fcEvents}
                height={fcHeight}
                contentHeight={fcHeight}
                firstDay={1}                 // Monday-first for UK
                weekNumbers={false}
                dayMaxEventRows={isNarrow ? 2 : 6}
                nowIndicator
                buttonText={{ today: "Today", month: "Month", week: "Week", day: "Day" }}
                eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false, meridiem: false }}
                slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false, meridiem: false }}
                eventClick={(info) => {
                  info.jsEvent.preventDefault();
                  // Yearly events are HQ-managed in their own modal —
                  // open it instead of the Google event editor.
                  if (info.event.extendedProps?._kind === "yearly") {
                    setYearlyOpen(true);
                    return;
                  }
                  // FullCalendar puts the id on the event itself, but our
                  // modal expects a flat object — merge them so the
                  // delete button can see event.id.
                  setModal({ event: { id: info.event.id, ...info.event.extendedProps } });
                }}
                datesSet={(info) => {
                  // Whenever the user clicks prev/next or switches view we
                  // refetch the visible window plus a 7-day buffer so events
                  // that start just before / after the boundary still show.
                  const start = new Date(info.start);
                  start.setDate(start.getDate() - 7);
                  const end = new Date(info.end);
                  end.setDate(end.getDate() + 7);
                  loadEvents({ start, end });
                  // Track the midpoint of the visible range so the
                  // Projects-this-month modal lines up with whatever
                  // month is on screen (works across Month/Week/Day).
                  const mid = new Date((info.start.getTime() + info.end.getTime()) / 2);
                  setVisibleDate(mid);
                }}
                dateClick={(info) => {
                  // Click a day cell → open new-event modal pre-filled to that
                  // date at 09:00–10:00 local
                  const d = new Date(info.dateStr);
                  const pad = (n) => String(n).padStart(2, "0");
                  const dateBase = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
                  setModal({ event: null, defaults: { start: `${dateBase}T09:00`, end: `${dateBase}T10:00` } });
                }}
              />
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white border border-dashed border-stone-300 rounded-2xl px-6 py-12 text-center">
              <CalendarDays className="w-10 h-10 mx-auto text-stone-300" />
              <p className="text-sm text-stone-600 mt-3">No upcoming events. Click <strong>New event</strong> above to create one.</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="cal-events">
              {(() => {
                // Detect the index where past starts so we can drop a
                // visual divider between "upcoming" and "past" rows.
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const todayLabel = today.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
                const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowLabel = tomorrow.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
                let pastShown = false;
                return grouped.map(([dayLabel, list], idx) => {
                  const sample = list[0]?.start ? new Date(list[0].start) : null;
                  const isPast = sample && new Date(sample.getFullYear(), sample.getMonth(), sample.getDate()).getTime() < today.getTime();
                  const showPastDivider = isPast && !pastShown;
                  if (isPast) pastShown = true;
                  return (
                    <div key={dayLabel}>
                      {showPastDivider && (
                        <div className="mb-3 flex items-center gap-3" data-testid="cal-past-divider">
                          <div className="flex-1 h-px bg-stone-200"></div>
                          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Past events</span>
                          <div className="flex-1 h-px bg-stone-200"></div>
                        </div>
                      )}
                      <div className={`text-[11px] uppercase tracking-[0.25em] font-bold mb-2 flex items-center gap-2 ${
                        dayLabel === todayLabel ? "text-blue-700" :
                        dayLabel === tomorrowLabel ? "text-emerald-700" :
                        isPast ? "text-stone-400" : "text-stone-500"
                      }`}>
                        {dayLabel}
                        {dayLabel === todayLabel && <span className="px-1.5 py-0.5 text-[9px] bg-blue-100 text-blue-800 rounded">Today</span>}
                        {dayLabel === tomorrowLabel && <span className="px-1.5 py-0.5 text-[9px] bg-emerald-100 text-emerald-800 rounded">Tomorrow</span>}
                      </div>
                      <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100 overflow-hidden">
                        {list.map((e) => (
                          <EventRow key={e.id} event={e} onEdit={() => setModal({ event: e })} onDelete={() => deleteEvent(e.id)} />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </>
      )}

      {modal && (
        <EventModal
          event={modal.event}
          defaults={modal.defaults}
          preset={modal.preset}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); setRefreshTick((t) => t + 1); }}
          onDelete={async (id) => { await deleteEvent(id); setModal(null); }}
        />
      )}
      {yearlyOpen && <YearlyEventsModal onClose={() => { setYearlyOpen(false); setRefreshTick((t) => t + 1); }} />}
      {projectsModalOpen && (
        <ProjectsThisMonthModal
          visibleDate={visibleDate}
          onClose={() => setProjectsModalOpen(false)}
        />
      )}
    </div>
  );
}

function EventRow({ event, onEdit, onDelete }) {
  return (
    <div className="px-5 py-4 flex items-start justify-between gap-4 hover:bg-stone-50" data-testid={`cal-event-${event.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold text-stone-950 truncate flex items-center gap-2">
          <span className="truncate">{event.title}</span>
          {event.show_in_portal && (
            <span
              data-testid={`cal-portal-badge-${event.id}`}
              title="Visible on the franchisee portal"
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-900 border border-emerald-300 px-1.5 py-0.5 rounded shrink-0"
            >
              <Users className="w-2.5 h-2.5" /> Portal
            </span>
          )}
        </div>
        <div className="text-xs text-stone-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <Clock className="w-3 h-3" /> {formatDateRange(event.start, event.end, event.all_day)}
          {event.location && (<><span className="text-stone-300">·</span><MapPin className="w-3 h-3" /> <span className="truncate">{event.location}</span></>)}
        </div>
        {event.description && <div className="text-xs text-stone-600 mt-2 line-clamp-2 whitespace-pre-wrap">{event.description}</div>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {event.meeting_url && (
          <a href={event.meeting_url} target="_blank" rel="noreferrer" data-testid={`cal-join-${event.id}`}
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md inline-flex items-center gap-1.5">
            <Video className="w-3 h-3" /> Join
          </a>
        )}
        {event.html_link && (
          <a href={event.html_link} target="_blank" rel="noreferrer" title="Open in Google Calendar"
            className="w-7 h-7 rounded-md border border-stone-300 hover:bg-stone-100 flex items-center justify-center">
            <ExternalLink className="w-3.5 h-3.5 text-stone-700" />
          </a>
        )}
        <button onClick={onEdit} data-testid={`cal-edit-${event.id}`} className="w-7 h-7 rounded-md border border-stone-300 hover:bg-stone-100 flex items-center justify-center" title="Edit">
          <Pencil className="w-3.5 h-3.5 text-stone-700" />
        </button>
        <button onClick={onDelete} data-testid={`cal-delete-${event.id}`} className="w-7 h-7 rounded-md border border-stone-300 hover:bg-red-50 hover:border-red-300 flex items-center justify-center" title="Delete">
          <Trash2 className="w-3.5 h-3.5 text-stone-700" />
        </button>
      </div>
    </div>
  );
}

function EventModal({ event, defaults, preset, onClose, onSaved, onDelete }) {
  const isMojoGrow = preset === "mojo_grow";
  const [title, setTitle] = useState(event?.title || (isMojoGrow ? "Mojo Grow Meeting" : ""));
  // Track whether the user has typed in the title — if they haven't,
  // we re-derive the title from the chosen start date when in
  // Mojo-Grow mode (e.g. "Mojo Grow Meeting · Fri 15 Aug · 7:00 PM").
  const titleManuallyEditedRef = useRef(false);
  const [description, setDescription] = useState(event?.description || "");
  const [location, setLocation] = useState(event?.location || (isMojoGrow ? "Online (Zoom)" : ""));
  const [meetingUrl, setMeetingUrl] = useState(event?.meeting_url || "");
  const [showInPortal, setShowInPortal] = useState(isMojoGrow ? true : !!event?.show_in_portal);
  // Orange-highlighted flag for initial franchisee meetings (potential
  // franchisees). Purely a visual cue on the admin calendar; no portal
  // audience implications, no email side-effects. Never applicable in
  // the "New Mojo Grow Meeting" modal — that panel is exclusively for
  // Mojo Grow meetings — so we hard-clear it in Mojo-Grow mode.
  const [initialFranchiseeMeeting, setInitialFranchiseeMeeting] = useState(
    isMojoGrow ? false : !!event?.initial_franchisee_meeting,
  );
  // Audience scope — "all" (default, visible to every franchisee) or
  // "selected" (only the franchisees in selectedFranchiseeIds see it).
  const initialIds = Array.isArray(event?.portal_franchisee_ids)
    ? event.portal_franchisee_ids : [];
  const [audienceMode, setAudienceMode] = useState(initialIds.length ? "selected" : "all");
  const [selectedFranchiseeIds, setSelectedFranchiseeIds] = useState(initialIds);
  const [franchiseesList, setFranchiseesList] = useState([]);
  const [franchiseesLoading, setFranchiseesLoading] = useState(false);
  const [franchiseeSearch, setFranchiseeSearch] = useState("");
  const [allDay, setAllDay] = useState(!!event?.all_day);
  const [start, setStart] = useState(event?.start ? event.start.slice(0, 16) : (defaults?.start || todayLocal()));
  const [end, setEnd] = useState(event?.end ? event.end.slice(0, 16) : (defaults?.end || todayLocal(60)));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoomNotice, setZoomNotice] = useState("");

  // Mojo Grow HQ Update title — editable so Sam can schedule multiple
  // meetings in one sitting without every announcement + email reading
  // the same "Next Mojo Grow Meeting". Only surfaced in the Mojo Grow
  // create modal; ignored on edits and non-Mojo-Grow events. Defaults
  // to the historical fixed phrasing so existing muscle memory works.
  const [hqTitle, setHqTitle] = useState("Next Mojo Grow Meeting");

  // Friendly end-time keeper — when the user edits the start, we slide
  // the end forward to maintain the same gap (or default to +60 mins),
  // matching what every other calendar app does.
  const setStartSmart = (newStart) => {
    if (!allDay && newStart && end) {
      const oldS = new Date(start).getTime();
      const oldE = new Date(end).getTime();
      const newS = new Date(newStart).getTime();
      if (Number.isFinite(oldS) && Number.isFinite(oldE) && Number.isFinite(newS)) {
        const gap = oldE > oldS ? oldE - oldS : 60 * 60 * 1000;
        const projected = new Date(newS + gap);
        // ISO local-style "YYYY-MM-DDTHH:mm" — strip timezone+seconds.
        const pad = (n) => String(n).padStart(2, "0");
        const iso = `${projected.getFullYear()}-${pad(projected.getMonth() + 1)}-${pad(projected.getDate())}T${pad(projected.getHours())}:${pad(projected.getMinutes())}`;
        setEnd(iso);
      }
    }
    setStart(newStart);
  };

  // Lazy-fetch the franchisee list the first time the admin switches
  // into the "selected franchisees only" mode. Cached for the modal's
  // lifetime so the picker is snappy.
  useEffect(() => {
    if (audienceMode !== "selected" || franchiseesList.length || franchiseesLoading) return;
    setFranchiseesLoading(true);
    api.get("/franchisees", { params: { limit: 500, sort_by: "organisation", sort_dir: 1 } })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.items || []);
        // Keep this list compact — only id + display name + organisation.
        // Drop ex-franchisees so the picker only offers live audiences.
        const slim = rows
          .filter((f) => (f.lifecycle_status || "active") !== "ex_franchisee")
          .map((f) => ({
            id: f.id,
            name: f.full_name || [f.first_name, f.last_name].filter(Boolean).join(" ") || f.organisation || f.email || f.mojo_email || "—",
            organisation: f.organisation || "",
            franchise_number: f.franchise_number || "",
            email: f.mojo_email || f.email || "",
            // Flag demo franchisees so the picker can badge them — they're
            // popular test targets but easy to miss in a long A-Z list.
            is_demo: Array.isArray(f.tags) && f.tags.some((t) => String(t).toLowerCase() === "demo"),
          }));
        // Pin demo franchisees to the top of the list so they're always
        // one click away from the admin's eye-line.
        slim.sort((a, b) => {
          if (a.is_demo && !b.is_demo) return -1;
          if (b.is_demo && !a.is_demo) return 1;
          return (a.organisation || a.name || "").localeCompare(b.organisation || b.name || "");
        });
        setFranchiseesList(slim);
      })
      .catch(() => { /* fall through — picker will show "Couldn't load" hint */ })
      .finally(() => setFranchiseesLoading(false));
  }, [audienceMode, franchiseesList.length, franchiseesLoading]);

  const toggleFranchiseeId = (id) => {
    setSelectedFranchiseeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Auto-derive the title from the chosen start date when in Mojo Grow
  // mode, but only as long as the admin hasn't typed their own override.
  // e.g. start "2026-08-15T19:00" → "Mojo Grow Meeting · Fri 15 Aug · 7:00 PM".
  useEffect(() => {
    if (!isMojoGrow || titleManuallyEditedRef.current || !start) return;
    try {
      const d = new Date(start);
      if (!Number.isFinite(d.getTime())) return;
      const day = d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
      const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
      setTitle(`Mojo Grow Meeting · ${day} · ${time}`);
    } catch { /* noop */ }
  }, [isMojoGrow, start]);

  // Treat the title as user-controlled the moment they type — stops the
  // auto-derive effect from clobbering their edits.
  const onTitleChange = (v) => {
    titleManuallyEditedRef.current = true;
    setTitle(v);
  };

  const save = async () => {
    setErr("");
    if (!title.trim()) { setErr("Title is required."); return; }
    if (!start || !end) { setErr("Pick start and end."); return; }
    // End must come after start — Google rejects an empty range with a
    // cryptic "time range is empty" error otherwise. Compare on raw
    // strings for all-day (yyyy-mm-dd) and timestamps for timed.
    if (allDay) {
      if (end < start) { setErr("End date must be on or after start date."); return; }
    } else {
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (Number.isFinite(s) && Number.isFinite(e) && e <= s) {
        setErr("End time must be after the start time.");
        return;
      }
    }
    setSaving(true);
    try {
      // ----- Mojo Grow: auto-mint a Zoom link if none has been set yet.
      // Reuses the same /zoom/meetings endpoint as the "Create Zoom"
      // button so the host (headoffice@), passcode policy, and audit log
      // all stay identical.
      let zoomUrlForEvent = meetingUrl;
      if (isMojoGrow && !zoomUrlForEvent) {
        try {
          // Duration in minutes between start/end (or 60 by default).
          let dur = 60;
          if (!allDay && start && end) {
            const s = new Date(start).getTime();
            const e = new Date(end).getTime();
            if (Number.isFinite(s) && Number.isFinite(e) && e > s) {
              dur = Math.max(15, Math.round((e - s) / 60000));
            }
          }
          const { data: zm } = await api.post("/zoom/meetings", {
            topic: title.trim(),
            start_time: new Date(start).toISOString().replace(/\.\d{3}Z$/, "Z"),
            duration: dur,
            timezone: "Europe/London",
            require_passcode: true,
            enable_waiting_room: false,
            agenda: description || "",
          });
          zoomUrlForEvent = zm?.join_url || "";
          if (zoomUrlForEvent) setMeetingUrl(zoomUrlForEvent);
        } catch (e) {
          // Don't block the event save if Zoom mint fails — surface a
          // warning but let the admin add a link manually afterwards.
          setZoomNotice("Couldn't auto-create Zoom link — please add it manually after saving.");
        }
      }

      // Audience scope — empty list when "show in portal" is off OR
      // when "all" is selected. The backend treats null/empty as "all
      // franchisees" (backwards-compatible with existing events).
      const portalIds = (showInPortal && audienceMode === "selected")
        ? selectedFranchiseeIds : [];
      const body = {
        title: title.trim(),
        description: description || null,
        location: location || null,
        meeting_url: zoomUrlForEvent ? zoomUrlForEvent.trim() : null,
        show_in_portal: showInPortal,
        initial_franchisee_meeting: initialFranchiseeMeeting,
        portal_franchisee_ids: portalIds,
        all_day: allDay,
        start: allDay ? start.slice(0, 10) : new Date(start).toISOString(),
        end: allDay ? end.slice(0, 10) : new Date(end).toISOString(),
      };
      let savedEvent = null;
      if (event?.id) {
        const { data } = await api.patch(`/calendar/events/${event.id}`, body);
        savedEvent = data || { id: event.id };
      } else {
        const { data } = await api.post("/calendar/events", body);
        savedEvent = data || null;
      }

      // ----- Mojo Grow: after the event saves, also create + auto-publish
      // an HQ Update so every active franchisee is emailed a "Next Mojo
      // Grow Meeting" note with the Zoom link. Fires ONLY on first
      // create (``!event?.id``) so editing an existing Mojo Grow event
      // doesn't re-blast the mailing list. Title/intro/panel shape is
      // fixed by product spec:
      //   * Title  : "Next Mojo Grow Meeting" (verbatim, per user)
      //   * Intro  : friendly greeting + the date + start time
      //   * Panel  : single link panel titled "Join the Zoom meeting"
      //              carrying the newly-minted Zoom join URL
      //   * Category: meetings
      //   * Recipients: all active franchisees (recipient_ids=null)
      // Two-step: draft then publish. Publish requires
      // ``confirm_send_all: true`` to bypass the "explicit confirm-send-
      // all" safety guardrail — that's intentional here because the
      // ADMIN just clicked "Save" on the meeting composer, i.e. they
      // have already confirmed the send by scheduling the meeting.
      if (isMojoGrow && zoomUrlForEvent && !event?.id && savedEvent?.id) {
        try {
          const startDate = new Date(start);
          const dayLabel = startDate.toLocaleDateString("en-GB", {
            weekday: "long", day: "2-digit", month: "long", year: "numeric",
          });
          const timeLabel = startDate.toLocaleTimeString("en-GB", {
            hour: "2-digit", minute: "2-digit", hour12: false,
          });
          // ``meeting_when`` — the formatted date+time string is now
          // threaded through to the notification email subject and
          // body, so recipients can see the schedule without opening
          // the portal. The HQ Update TITLE is admin-editable via
          // ``hqTitle`` (default "Next Mojo Grow Meeting") — no
          // automatic date/time chip is appended anymore, per Sam's
          // Feb 2026 refinement.
          const meetingWhen = `${dayLabel} at ${timeLabel}`;
          const cleanHqTitle = (hqTitle || "").trim() || "Next Mojo Grow Meeting";
          // Skip the draft-then-publish two-step and post directly to
          // the create+send endpoint. Mojo Grow meetings are always
          // published immediately (Sam already clicked "Save" on the
          // meeting composer — that's the confirm-send-all gesture).
          // Going through /draft first left a stale DRAFT row on the
          // HQ Updates admin list, which is confusing.
          await api.post("/admin/announcements", {
            title: cleanHqTitle,
            intro: `Hi everyone,\n\nThe next Mojo Grow meeting is scheduled for ${dayLabel} at ${timeLabel}.\n\nUse the Zoom link below to join us.`,
            category: "meetings",
            // Fresh Zoom link on a Mojo Grow meeting = "New Meeting"
            // notification. Catch-Up replays come later via the manual
            // HQ Updates composer where the admin picks the kind.
            meeting_kind: "new",
            meeting_when: meetingWhen,
            recipient_ids: null,
            confirm_send_all: true,
            panels: [{
              kind: "link",
              title: "Join the Zoom meeting",
              blurb: `${dayLabel} · ${timeLabel}`,
              resolved_url: zoomUrlForEvent,
              url: zoomUrlForEvent,
            }],
            source: "mojo_grow_meeting",
            source_calendar_event_id: savedEvent.id,
          });
        } catch (e) {
          // Non-fatal — the calendar event itself saved. Surface a hint
          // so the admin can retry from the HQ Updates page.
          setZoomNotice((n) => (n ? n + " " : "")
            + "Calendar event saved, but the HQ Update to franchisees couldn't be sent automatically — please send it manually from the HQ Updates page.");
        }
      }
      onSaved?.();
    } catch (e) {
      // Surface the user-friendly translation for the most common
      // Google API gotcha instead of dumping the raw HttpError.
      const raw = e?.response?.data?.detail || "Save failed";
      if (typeof raw === "string" && /timeRangeEmpty|time range is empty/i.test(raw)) {
        setErr("End time must be after the start time.");
      } else {
        setErr(raw);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-stone-950/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="cal-event-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[calc(100vh-3rem)]">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between shrink-0">
          <span className="font-bold text-stone-950">{event ? "Edit event" : (isMojoGrow ? "New Mojo Grow Meeting" : "New event")}</span>
          <button onClick={onClose} className="w-8 h-8 hover:bg-stone-100 rounded-md flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {isMojoGrow && !event && (
            <div className="bg-[#dddd16]/20 border border-[#dddd16] rounded-lg p-3 text-xs text-stone-800 space-y-1" data-testid="mojo-grow-notice">
              <div className="font-bold inline-flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Mojo Grow setup</div>
              <div>On save we&apos;ll:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Auto-create a Zoom link for the chosen start time</li>
                <li>Publish this event to the Franchisee Portal calendar for all franchisees</li>
                <li>Send an HQ Update titled &ldquo;{(hqTitle || "").trim() || "Next Mojo Grow Meeting"}&rdquo; to every active franchisee with the meeting date, time and Zoom link</li>
              </ul>
            </div>
          )}
          {isMojoGrow && !event && (
            <div data-testid="mojo-grow-hq-title-block">
              <Input
                label="HQ Update title (editable)"
                value={hqTitle}
                onChange={setHqTitle}
                placeholder="Next Mojo Grow Meeting"
                testid="mojo-grow-hq-title"
              />
              <div className="text-[11px] text-stone-500 mt-1">
                This is the title franchisees will see in their HQ Updates
                feed. The meeting date and time are added to the notification
                email automatically.
              </div>
            </div>
          )}
          <Input label="Title" value={title} onChange={onTitleChange} placeholder="Quarterly franchisee call" testid="cal-title" />
          <Input label="Location (optional)" value={location} onChange={setLocation} placeholder="Online / 12 High St, Cullompton" testid="cal-location" />
          <label className="flex items-center gap-2 text-xs text-stone-700">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="cal-all-day" />
            All-day event
          </label>
          <div className="grid grid-cols-2 gap-3">
            <DateInput label="Starts" value={start} onChange={setStartSmart} allDay={allDay} testid="cal-start" />
            <DateInput label="Ends" value={end} onChange={setEnd} allDay={allDay} testid="cal-end" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Meeting link (optional)</label>
              <button
                type="button"
                onClick={() => setZoomModalOpen(true)}
                data-testid="cal-create-zoom-btn"
                className="text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-md bg-[#2D8CFF] text-white hover:bg-[#1A73D9] transition-colors flex items-center gap-1.5"
                title="Create a Zoom meeting on headoffice@creativemojo.co.uk"
              >
                <Video className="w-3 h-3" />
                {meetingUrl ? "Replace with Zoom" : "Create Zoom meeting"}
              </button>
            </div>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
              <input
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                placeholder="Paste a Teams / Zoom / Meet link, or click 'Create Zoom meeting' →"
                data-testid="cal-meeting-url"
                className="w-full pl-9 pr-3 py-2 text-sm border border-stone-300 rounded-lg"
              />
            </div>
            {meetingUrl && meetingUrl.trim() && (
              /* Prominent one-click join for existing events — avoids the
                 old copy-paste dance when jumping into the meeting from
                 the admin calendar. ``noopener`` so the join tab can't
                 script back into the Hub. */
              <a
                href={meetingUrl.trim()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cal-modal-join-meeting"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider bg-[#2D8CFF] text-white hover:bg-[#1A73D9] rounded-lg shadow-sm transition-colors"
                title="Open the meeting link in a new tab"
              >
                <Video className="w-4 h-4" /> Join this meeting
              </a>
            )}
            {zoomNotice && (
              <div className="mt-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-md flex items-center gap-1.5" data-testid="cal-zoom-notice">
                <CheckCircle2 className="w-3 h-3" /> {zoomNotice}
              </div>
            )}
          </div>
          {/* Initial franchisee meeting — orange highlight on the admin
              calendar so early-stage prospect conversations stand out
              from the day-to-day HQ stuff. Admin-only, no portal effect.
              Hidden entirely in the "New Mojo Grow Meeting" modal —
              this panel is exclusively for Mojo Grow meetings. */}
          {!isMojoGrow && (
          <label className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${initialFranchiseeMeeting ? "border-orange-400 bg-orange-50/60" : "border-stone-200 hover:border-stone-300 bg-white"}`}
            data-testid="cal-initial-fran-toggle-label">
            <input
              type="checkbox"
              checked={initialFranchiseeMeeting}
              onChange={(e) => setInitialFranchiseeMeeting(e.target.checked)}
              data-testid="cal-initial-fran-toggle"
              className="mt-0.5 accent-orange-600"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-stone-950 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-sm bg-orange-500" aria-hidden="true" />
                Initial franchisee meeting
              </div>
              <div className="text-[11px] text-stone-600 mt-0.5">
                Highlights this entry in orange on the admin calendar so meetings with potential franchisees are easy to spot at a glance.
              </div>
            </div>
          </label>
          )}

          {/* Franchisee-portal toggle — events default to admin-only.
              Tick this to also surface the event on the franchisee
              portal's Events panel (shared join URL, time, description). */}
          <label className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${showInPortal ? "border-emerald-400 bg-emerald-50/60" : "border-stone-200 hover:border-stone-300 bg-white"}`}
            data-testid="cal-portal-toggle-label">
            <input
              type="checkbox"
              checked={showInPortal}
              onChange={(e) => setShowInPortal(e.target.checked)}
              data-testid="cal-portal-toggle"
              className="mt-0.5 accent-emerald-600"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-stone-950">
                Also include in Franchisee Portal Calendar
              </div>
              <div className="text-[11px] text-stone-600 mt-0.5">
                {showInPortal
                  ? "This event will appear in the franchisee portal — see the audience options below."
                  : "Admin-only — won't appear on the franchisee portal."}
              </div>
            </div>
          </label>

          {/* Audience scope — only shown once "show in portal" is on. */}
          {showInPortal && (
            <div className="ml-3 pl-4 border-l-2 border-emerald-200 space-y-3" data-testid="cal-portal-audience">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Who can see this on the portal?</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className={`flex items-start gap-2.5 p-3 border rounded-lg cursor-pointer transition text-sm ${audienceMode === "all" ? "border-stone-950 bg-stone-50 ring-1 ring-stone-950" : "border-stone-200 hover:border-stone-300 bg-white"}`}>
                  <input
                    type="radio"
                    name="cal-audience"
                    checked={audienceMode === "all"}
                    onChange={() => setAudienceMode("all")}
                    data-testid="cal-audience-all"
                    className="mt-0.5 accent-stone-950"
                  />
                  <div>
                    <div className="font-bold text-stone-950">All franchisees</div>
                    <div className="text-[11px] text-stone-600 mt-0.5">Default. Visible to every franchisee with portal access.</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2.5 p-3 border rounded-lg cursor-pointer transition text-sm ${audienceMode === "selected" ? "border-stone-950 bg-stone-50 ring-1 ring-stone-950" : "border-stone-200 hover:border-stone-300 bg-white"}`}>
                  <input
                    type="radio"
                    name="cal-audience"
                    checked={audienceMode === "selected"}
                    onChange={() => setAudienceMode("selected")}
                    data-testid="cal-audience-selected"
                    className="mt-0.5 accent-stone-950"
                  />
                  <div>
                    <div className="font-bold text-stone-950">Selected franchisees only</div>
                    <div className="text-[11px] text-stone-600 mt-0.5">Tick the franchisees who should see this event.</div>
                  </div>
                </label>
              </div>

              {audienceMode === "selected" && (
                <div className="bg-white border border-stone-200 rounded-lg" data-testid="cal-franchisee-picker">
                  <div className="px-3 py-2 border-b border-stone-100 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-bold text-stone-700">
                      {selectedFranchiseeIds.length === 0
                        ? "No franchisees selected yet"
                        : `${selectedFranchiseeIds.length} selected`}
                    </div>
                    {selectedFranchiseeIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedFranchiseeIds([])}
                        className="text-[11px] text-stone-500 hover:text-stone-900 underline"
                        data-testid="cal-franchisee-clear"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  {/* Search bar — filters by organisation, name, franchise number, or email. */}
                  {franchiseesList.length > 0 && (
                    <div className="px-3 py-2 border-b border-stone-100">
                      <input
                        type="search"
                        value={franchiseeSearch}
                        onChange={(e) => setFranchiseeSearch(e.target.value)}
                        placeholder="Search by organisation, name, #, or email…"
                        data-testid="cal-franchisee-search"
                        className="w-full px-2.5 py-1.5 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-950"
                      />
                    </div>
                  )}
                  {franchiseesLoading ? (
                    <div className="px-3 py-6 text-center text-stone-500 text-xs"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading franchisees…</div>
                  ) : franchiseesList.length === 0 ? (
                    <div className="px-3 py-6 text-center text-stone-500 text-xs">Couldn't load franchisees. Save anyway — defaults to all.</div>
                  ) : (() => {
                    const q = franchiseeSearch.trim().toLowerCase();
                    const filtered = q ? franchiseesList.filter((f) =>
                      (f.organisation || "").toLowerCase().includes(q)
                      || (f.name || "").toLowerCase().includes(q)
                      || (f.franchise_number || "").toLowerCase().includes(q)
                      || (f.email || "").toLowerCase().includes(q)
                    ) : franchiseesList;
                    if (filtered.length === 0) {
                      return <div className="px-3 py-6 text-center text-stone-500 text-xs">No franchisees match "{franchiseeSearch}".</div>;
                    }
                    return (
                      <div className="max-h-56 overflow-y-auto divide-y divide-stone-100">
                        {filtered.map((f) => {
                          const checked = selectedFranchiseeIds.includes(f.id);
                          return (
                            <label
                              key={f.id}
                              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm hover:bg-stone-50 ${checked ? "bg-emerald-50/60" : ""}`}
                              data-testid={`cal-franchisee-opt-${f.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFranchiseeId(f.id)}
                                className="accent-emerald-600"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-stone-950 font-medium truncate flex items-center gap-1.5">
                                  <span className="truncate">{f.organisation || f.name}</span>
                                  {f.is_demo && (
                                    <span className="shrink-0 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#dedd0a] text-stone-950">Demo</span>
                                  )}
                                </div>
                                <div className="text-[11px] text-stone-500 truncate">
                                  {f.franchise_number ? `#${f.franchise_number}` : ""}
                                  {f.franchise_number && f.organisation ? " · " : ""}
                                  {f.organisation ? f.name : ""}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">Description (optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="cal-description"
              placeholder="Agenda, attendee notes, links…"
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg" />
          </div>
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> {err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-stone-200 flex justify-between items-center gap-2 bg-stone-50 shrink-0 rounded-b-2xl">
          {event?.id && onDelete ? (
            <button
              onClick={() => {
                if (window.confirm("Delete this calendar event? It will also disappear from the franchisee portal.")) {
                  onDelete(event.id);
                }
              }}
              data-testid="cal-modal-delete"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-lg border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 text-xs font-bold rounded-lg border border-stone-300 bg-white hover:bg-stone-50">Cancel</button>
            <button onClick={save} disabled={saving || !title.trim()} data-testid="cal-save"
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {event ? "Save changes" : "Create event"}
            </button>
          </div>
        </div>
      </div>
      {zoomModalOpen && (
        <ZoomMeetingModal
          defaultTopic={title}
          defaultStart={start}
          defaultEnd={end}
          allDay={allDay}
          onClose={() => setZoomModalOpen(false)}
          onCreated={({ join_url, password, topic }) => {
            setMeetingUrl(join_url);
            if (!title.trim() && topic) setTitle(topic);
            setZoomNotice(password ? `Zoom meeting created — passcode ${password}` : "Zoom meeting created");
            setZoomModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

function ZoomMeetingModal({ defaultTopic, defaultStart, defaultEnd, allDay, onClose, onCreated }) {
  // Compute a sensible default duration in minutes from start→end.
  const defaultDuration = (() => {
    if (allDay) return 60;
    const s = new Date(defaultStart).getTime();
    const e = new Date(defaultEnd).getTime();
    if (Number.isFinite(s) && Number.isFinite(e) && e > s) {
      return Math.max(5, Math.min(1440, Math.round((e - s) / 60000)));
    }
    return 60;
  })();

  const [duration, setDuration] = useState(defaultDuration);
  // Per-product spec: passcode is always OFF (Zoom forces one anyway if
  // the account-level setting requires it, but we don't surface the
  // toggle to the admin). Waiting room is the security guard — default
  // ON so franchisees don't crash into a meeting that hasn't started,
  // but the admin can still untick it for casual drop-ins.
  const [waitingRoom, setWaitingRoom] = useState(true);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    setErr("");
    if (allDay) {
      setErr("Zoom needs a specific start time — switch off 'All-day' first.");
      return;
    }
    setCreating(true);
    try {
      const startIso = new Date(defaultStart).toISOString();
      const { data } = await api.post("/zoom/meetings", {
        topic: (defaultTopic && defaultTopic.trim()) || "Creative Mojo meeting",
        start_time: startIso,
        duration,
        timezone: "Europe/London",
        require_passcode: false,
        enable_waiting_room: waitingRoom,
        agenda: defaultTopic || null,
      });
      onCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Zoom meeting creation failed.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-stone-950/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="zoom-create-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between bg-[#2D8CFF] text-white rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            <span className="font-bold">Create Zoom meeting</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 hover:bg-white/20 rounded-md flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-[11px] text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 leading-relaxed">
            Hosted on <strong>headoffice@creativemojo.co.uk</strong>. The join URL will auto-fill the meeting-link field. Daily Zoom limit: 100 meetings.
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">Duration (minutes)</label>
            <input
              type="number"
              min={5}
              max={1440}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || 0)}
              data-testid="zoom-duration"
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg tabular-nums"
            />
          </div>

          <label className={`flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${waitingRoom ? "border-blue-400 bg-blue-50/60" : "border-stone-200 hover:border-stone-300 bg-white"}`}>
            <input
              type="checkbox"
              checked={waitingRoom}
              onChange={(e) => setWaitingRoom(e.target.checked)}
              data-testid="zoom-waiting-room"
              className="mt-0.5 accent-blue-600"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-stone-950">Enable waiting room</div>
              <div className="text-[11px] text-stone-600 mt-0.5">
                Attendees see a holding screen until the host admits them.
              </div>
            </div>
          </label>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> {err}
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-stone-200 flex justify-end gap-2 bg-stone-50 rounded-b-2xl">
          <button onClick={onClose} className="px-3 py-2 text-xs font-bold rounded-lg border border-stone-300 bg-white hover:bg-stone-50">
            Cancel
          </button>
          <button
            onClick={create}
            disabled={creating}
            data-testid="zoom-create-confirm"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#2D8CFF] text-white hover:bg-[#1A73D9] rounded-lg disabled:opacity-50 flex items-center gap-1.5"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {creating ? "Creating…" : "Create meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid, icon: Icon }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />}
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2 text-sm border border-stone-300 rounded-lg`} />
      </div>
    </div>
  );
}

function DateInput({ label, value, onChange, allDay, testid }) {
  // For datetime fields we replace the browser's native datetime-local
  // input with a date picker + dedicated hour/minute selects. The
  // native picker's spinner ignores `step="900"` and shows every minute
  // 0-59, which made it too easy for admins to pick an awkward time
  // like 14:23. The constrained selects enforce 06-22 hours and
  // 15-minute increments by construction.
  const parts = (value || "").split("T");
  const datePart = parts[0] || "";
  const timePart = parts[1] || "09:00";
  const [hhStr, mmStr] = timePart.split(":");
  const hh = Number.parseInt(hhStr || "9", 10);
  const mm = Number.parseInt(mmStr || "0", 10);

  // Snap hour into 6..22 range, minute to 00/15/30/45 — used when we
  // parse an externally-set value that doesn't already conform.
  const snapHour = (n) => Math.min(22, Math.max(6, Number.isFinite(n) ? n : 9));
  const snapMin = (n) => {
    if (!Number.isFinite(n)) return 0;
    const buckets = [0, 15, 30, 45];
    return buckets.reduce((best, b) => (Math.abs(n - b) < Math.abs(n - best) ? b : best), 0);
  };

  const emit = (d, h, m) => {
    if (!d) return;
    if (allDay) { onChange(d); return; }
    onChange(`${d}T${String(snapHour(h)).padStart(2, "0")}:${String(snapMin(m)).padStart(2, "0")}`);
  };

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          value={datePart}
          onChange={(e) => emit(e.target.value, hh, mm)}
          data-testid={testid}
          className="flex-1 px-3 py-2 text-sm border border-stone-300 rounded-lg tabular-nums"
        />
        {!allDay && (
          <>
            <select
              value={snapHour(hh)}
              onChange={(e) => emit(datePart, Number(e.target.value), mm)}
              data-testid={`${testid}-hour`}
              aria-label="Hour"
              className="px-2 py-2 text-sm border border-stone-300 rounded-lg tabular-nums bg-white"
            >
              {Array.from({ length: 22 - 6 + 1 }, (_, i) => 6 + i).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
              ))}
            </select>
            <span className="text-stone-500 font-bold">:</span>
            <select
              value={snapMin(mm)}
              onChange={(e) => emit(datePart, hh, Number(e.target.value))}
              data-testid={`${testid}-minute`}
              aria-label="Minute"
              className="px-2 py-2 text-sm border border-stone-300 rounded-lg tabular-nums bg-white"
            >
              {[0, 15, 30, 45].map((m) => (
                <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
              ))}
            </select>
          </>
        )}
      </div>
    </div>
  );
}

function SetupPanel({ status }) {
  return (
    <div className="bg-white border border-amber-300 bg-amber-50/60 rounded-2xl p-6" data-testid="cal-setup-required">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-stone-800 space-y-3 leading-relaxed">
          <div className="font-bold text-stone-950">Google Calendar not configured yet</div>
          <ol className="list-decimal pl-5 space-y-2 text-stone-700">
            <li>Open <a className="underline text-stone-950" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> → create or pick a project.</li>
            <li>APIs & Services → Library → enable <strong>Google Calendar API</strong>.</li>
            <li>OAuth consent screen → External → add your email as a test user. Add scope <code className="bg-stone-100 px-1 rounded">https://www.googleapis.com/auth/calendar</code>.</li>
            <li>Credentials → Create credentials → OAuth client ID → <strong>Web application</strong>. Add this redirect URI exactly: <br/><code className="bg-stone-100 px-1 rounded text-[11px]">{status?.redirect_uri}</code></li>
            <li>Copy the <strong>Client ID</strong> and <strong>Client secret</strong> + your calendar's ID (Settings → Integrate calendar) and send them over. I'll drop them into <code className="bg-stone-100 px-1 rounded">backend/.env</code> as <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code>, <code>GOOGLE_CALENDAR_ID</code>.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
